import { formatCurrency } from '@/utils/formatCurrency';
import {
  activeMarketplaceBudgetMin,
  budgetPeriodFromType,
  type BudgetPeriod,
} from '@/utils/marketplaceBudgetLimits';
import type { CampaignAdProduct } from '@/types/spCampaigns';

export function campaignBudgetMin(
  adProduct: CampaignAdProduct = 'SP',
  period: BudgetPeriod = 'daily'
): number | null {
  return activeMarketplaceBudgetMin(adProduct, period);
}

export function defaultCampaignBudgetInput(adProduct: CampaignAdProduct = 'SP'): string {
  const min = campaignBudgetMin(adProduct);
  return min == null ? '' : String(min);
}

export function validateCampaignBudget(
  value: number,
  adProduct: CampaignAdProduct = 'SP',
  period: BudgetPeriod | string = 'daily',
  label?: string
): string | null {
  const resolvedPeriod = budgetPeriodFromType(period);
  const amountLabel =
    label ?? (resolvedPeriod === 'lifetime' ? 'Lifetime budget' : 'Daily budget');
  if (Number.isNaN(value)) return `${amountLabel} must be a valid number.`;
  const min = campaignBudgetMin(adProduct, resolvedPeriod);
  if (min == null || value >= min) return null;
  return `${amountLabel} must be at least ${formatCurrency(min)}.`;
}

export function defaultAdGroupName(campaignName: string): string {
  const trimmed = campaignName.trim();
  return trimmed ? `${trimmed} - Ad Group` : '';
}

export function toIsoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

export function isoDateToSd(iso: string): string {
  return iso.replace(/-/g, '');
}

export function defaultCampaignDateRange(): { startDate: string; endDate: string } {
  const today = new Date();
  const end = new Date(today);
  end.setDate(end.getDate() + 30);
  return { startDate: toIsoDate(today), endDate: toIsoDate(end) };
}

export function formatCreateCampaignUpstreamError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const ax = err as { response?: { data?: { detail?: unknown } } };
    const d = ax.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      if (typeof o.hint === 'string') return o.hint;
      if (o.amazon && typeof o.amazon === 'object') {
        return JSON.stringify(o.amazon);
      }
      return JSON.stringify(d);
    }
  }
  if (err instanceof Error) return err.message;
  return 'Request failed';
}

export function extractSpSbCampaignMutateMessages(data: unknown): {
  errors: string[];
  successIds: string[];
} {
  const errors: string[] = [];
  const successIds: string[] = [];
  const seen = new Set<string>();
  const pushError = (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    errors.push(trimmed);
  };
  if (!data || typeof data !== 'object') return { errors, successIds };
  const root = data as Record<string, unknown>;
  const campaigns = root.campaigns;
  if (!campaigns || typeof campaigns !== 'object') return { errors, successIds };
  const c = campaigns as Record<string, unknown>;
  const errList = c.error;
  if (Array.isArray(errList)) {
    for (const item of errList) {
      if (!item || typeof item !== 'object') {
        errors.push(String(item));
        continue;
      }
      const e = item as Record<string, unknown>;
      const parts: string[] = [];
      if (e.index !== undefined) parts.push(`index ${String(e.index)}`);
      if (typeof e.code === 'string') parts.push(e.code);
      if (typeof e.description === 'string') parts.push(e.description);
      const nestedErrors = e.errors;
      if (Array.isArray(nestedErrors)) {
        for (const nested of nestedErrors) {
          if (!nested || typeof nested !== 'object') continue;
          const n = nested as Record<string, unknown>;
          const t = typeof n.errorType === 'string' ? n.errorType : null;
          const value = n.errorValue;
          if (value && typeof value === 'object') {
            for (const v of Object.values(value as Record<string, unknown>)) {
              if (!v || typeof v !== 'object') continue;
              const vv = v as Record<string, unknown>;
              const msg = typeof vv.message === 'string' ? vv.message : null;
              const reason = typeof vv.reason === 'string' ? vv.reason : null;
              if (msg && reason) {
                pushError(`${parts.join(': ')}${parts.length ? ': ' : ''}${msg} (${reason})`);
              } else if (msg) {
                pushError(`${parts.join(': ')}${parts.length ? ': ' : ''}${msg}`);
              } else if (reason) {
                pushError(`${parts.join(': ')}${parts.length ? ': ' : ''}${reason}`);
              }
            }
          }
          if (t) {
            pushError(`${parts.join(': ')}${parts.length ? ': ' : ''}${t}`);
          }
        }
      }
      if (parts.length) {
        pushError(parts.join(': '));
      } else {
        pushError(JSON.stringify(item));
      }
    }
  }
  const ok = c.success;
  if (Array.isArray(ok)) {
    for (const item of ok) {
      if (item && typeof item === 'object') {
        const id = (item as Record<string, unknown>).campaignId;
        if (typeof id === 'string' && id) successIds.push(id);
        else if (typeof id === 'number') successIds.push(String(id));
      }
    }
  }
  return { errors, successIds };
}

export function extractSdCampaignMutateMessages(data: unknown): {
  errors: string[];
  successIds: string[];
} {
  const errors: string[] = [];
  const successIds: string[] = [];
  if (!Array.isArray(data)) return { errors, successIds };
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const code = typeof row.code === 'string' ? row.code.toUpperCase() : '';
    const id = row.campaignId;
    if (code === 'SUCCESS' && (typeof id === 'string' || typeof id === 'number')) {
      successIds.push(String(id));
    } else if (code && code !== 'SUCCESS') {
      const desc = typeof row.description === 'string' ? row.description : code;
      errors.push(desc);
    }
  }
  return { errors, successIds };
}

export function extractAdGroupMutationResults(data: unknown): {
  errors: string[];
  successIds: string[];
} {
  const errors: string[] = [];
  const successIds: string[] = [];
  if (!data || typeof data !== 'object') return { errors, successIds };
  const root = data as Record<string, unknown>;
  const results = root.results;
  if (!Array.isArray(results)) return { errors, successIds };
  for (const item of results) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const status = typeof row.status === 'string' ? row.status.toLowerCase() : '';
    const id = row.adGroupId;
    if (status === 'success' && (typeof id === 'string' || typeof id === 'number')) {
      successIds.push(String(id));
    } else if (status === 'error') {
      const message = typeof row.message === 'string' ? row.message : 'Ad group creation failed.';
      errors.push(message);
    }
  }
  return { errors, successIds };
}

export function extractSbKeywordCreateResults(data: unknown): {
  errors: string[];
  successIds: string[];
} {
  const errors: string[] = [];
  const successIds: string[] = [];
  if (!data || typeof data !== 'object') return { errors, successIds };
  const root = data as Record<string, unknown>;
  const results = root.results;
  if (!Array.isArray(results)) return { errors, successIds };
  for (const item of results) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const status = typeof row.status === 'string' ? row.status.toLowerCase() : '';
    const id = row.keywordId;
    if (status === 'success' && (typeof id === 'string' || typeof id === 'number')) {
      successIds.push(String(id));
    } else if (status === 'error') {
      const message = typeof row.message === 'string' ? row.message : 'Keyword creation failed.';
      errors.push(message);
    }
  }
  return { errors, successIds };
}

export type BrandOption = {
  brandId?: string;
  brandEntityId: string;
  brandRegistryName?: string;
};

export function parseBrandsPayload(data: unknown): BrandOption[] {
  const root = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(root?.brands)
      ? root.brands
      : Array.isArray(root?.data)
        ? root.data
        : null;
  if (!raw) return [];
  return raw
    .filter((b): b is Record<string, unknown> => !!b && typeof b === 'object')
    .map((b) => {
      const brandEntityId = b.brandEntityId != null ? String(b.brandEntityId).trim() : '';
      const brandRegistryName =
        typeof b.brandRegistryName === 'string' ? b.brandRegistryName.trim() : undefined;
      return {
        brandId: typeof b.brandId === 'string' ? b.brandId : undefined,
        brandEntityId,
        brandRegistryName: brandRegistryName || undefined,
      };
    })
    // Vendors often omit brandEntityId; SB creatives still need brandRegistryName.
    .filter((b) => Boolean(b.brandEntityId || b.brandRegistryName));
}
